import React, { useState, useCallback, useEffect } from 'react';
import { useDropzone } from 'react-dropzone';
import axios from 'axios';
import './Attachments.css';
import { useAuth } from '../contexts/AuthContext';

const AttachmentsTab = ({ entityType, entityId, token }) => {
  const [attachments, setAttachments] = useState([]);
  const [grantAttachments, setGrantAttachments] = useState([]);
  const [loading, setLoading] = useState(false);
  const [loadingGrants, setLoadingGrants] = useState(false);
  const [uploading, setUploading] = useState(false);
  const [error, setError] = useState(null);
  const [success, setSuccess] = useState(null);
  const { isAdmin } = useAuth();

  // Get configuration from environment or use defaults
  const MAX_FILE_SIZE = parseInt(import.meta.env.VITE_MAX_FILE_SIZE || 15728640, 10);
  const ALLOWED_EXTENSIONS = (import.meta.env.VITE_ALLOWED_FILE_EXTENSIONS || 'pdf,docx')
    .split(',')
    .map(e => e.trim().toLowerCase());

  // Fetch attachments on mount or when entity changes
  useEffect(() => {
    fetchAttachments();
    if (entityType === 'organization') {
      fetchGrantAttachments();
    }
  }, [entityType, entityId]);

  const fetchAttachments = async () => {
    try {
      setLoading(true);
      setError(null);
      const response = await axios.get(
        `/api/attachments?entityType=${entityType}&entityId=${entityId}`,
        {
          headers: {
            Authorization: `Bearer ${token}`
          }
        }
      );
      setAttachments(response.data.data || []);
    } catch (err) {
      setError('Failed to load attachments');
      console.error('Error fetching attachments:', err);
    } finally {
      setLoading(false);
    }
  };

  const fetchGrantAttachments = async () => {
    try {
      setLoadingGrants(true);
      const response = await axios.get(
        `/api/attachments/organization/${entityId}/grants`,
        {
          headers: {
            Authorization: `Bearer ${token}`
          }
        }
      );
      setGrantAttachments(response.data.data || []);
    } catch (err) {
      console.error('Error fetching grant attachments:', err);
      setGrantAttachments([]);
    } finally {
      setLoadingGrants(false);
    }
  };

  const onDrop = useCallback(async (acceptedFiles) => {
    setError(null);
    setSuccess(null);

    if (acceptedFiles.length === 0) {
      return;
    }

    const file = acceptedFiles[0];

    // Validate extension
    const fileExtension = file.name.split('.').pop().toLowerCase();
    if (!ALLOWED_EXTENSIONS.includes(fileExtension)) {
      setError(`Invalid file type. Allowed types: ${ALLOWED_EXTENSIONS.join(', ')}`);
      return;
    }

    // Validate file size
    if (file.size > MAX_FILE_SIZE) {
      setError(`File size exceeds maximum limit of ${(MAX_FILE_SIZE / 1024 / 1024).toFixed(2)} MB`);
      return;
    }

    try {
      setUploading(true);

      // Convert file to base64
      const reader = new FileReader();
      reader.onload = async (e) => {
        const base64Data = e.target.result.split(',')[1];

        try {
          const response = await axios.post(
            `/api/attachments`,
            {
              entityType,
              entityId,
              filename: file.name,
              originalFilename: file.name,
              extension: fileExtension,
              size: file.size,
              fileData: base64Data
            },
            {
              headers: {
                Authorization: `Bearer ${token}`,
                'Content-Type': 'application/json'
              }
            }
          );

          setSuccess(`File "${file.name}" uploaded successfully`);
          // Refresh attachments list
          await fetchAttachments();
        } catch (uploadErr) {
          const errorMsg = uploadErr.response?.data?.message || 'Failed to upload file';
          setError(errorMsg);
          console.error('Upload error:', uploadErr);
        } finally {
          setUploading(false);
        }
      };
      reader.readAsDataURL(file);
    } catch (err) {
      setError('Failed to process file');
      setUploading(false);
    }
  }, [entityType, entityId, token, MAX_FILE_SIZE, ALLOWED_EXTENSIONS]);

  const { getRootProps, getInputProps, isDragActive } = useDropzone({
    onDrop,
    accept: {
      'application/pdf': ['.pdf'],
      'application/vnd.openxmlformats-officedocument.wordprocessingml.document': ['.docx']
    },
    maxFiles: 1,
    disabled: uploading
  });

  const downloadAttachment = async (attachment) => {
    try {
      const response = await axios.get(
        `/api/attachments/${attachment.id}/download`,
        {
          headers: {
            Authorization: `Bearer ${token}`
          },
          responseType: 'blob'
        }
      );

      // Create download link
      const url = window.URL.createObjectURL(new Blob([response.data]));
      const link = document.createElement('a');
      link.href = url;
      link.setAttribute('download', attachment.original_filename);
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
      window.URL.revokeObjectURL(url);
    } catch (err) {
      setError('Failed to download file');
      console.error('Download error:', err);
    }
  };

  const viewAttachment = async (attachment) => {
    try {
      const response = await axios.get(
        `/api/attachments/${attachment.id}/download`,
        {
          headers: {
            Authorization: `Bearer ${token}`
          },
          responseType: 'blob'
        }
      );

      // Open file in new tab
      const url = window.URL.createObjectURL(new Blob([response.data]));
      window.open(url, '_blank');
    } catch (err) {
      setError('Failed to view file');
      console.error('View error:', err);
    }
  };

  return (
    <div className="attachments-tab">
      <div className="attachments-container">
        <h3>Attachments</h3>

        {/* Upload Dropzone */}
        <div
          {...getRootProps()}
          className={`dropzone ${isDragActive ? 'active' : ''} ${uploading ? 'disabled' : ''}`}
        >
          <input {...getInputProps()} />
          <div className="dropzone-content">
            {uploading ? (
              <>
                <p>Uploading...</p>
                <div className="spinner"></div>
              </>
            ) : isDragActive ? (
              <p>Drop the file here...</p>
            ) : (
              <>
                <p className="dropzone-text">Drag and drop a file here, or click to select</p>
                <p className="dropzone-hint">
                  Allowed: {ALLOWED_EXTENSIONS.join(', ').toUpperCase()} (Max {(MAX_FILE_SIZE / 1024 / 1024).toFixed(2)} MB)
                </p>
              </>
            )}
          </div>
        </div>

        {/* Status Messages */}
        {error && <div className="error-message">{error}</div>}
        {success && <div className="success-message">{success}</div>}

        {/* Attachments List */}
        <div className="attachments-list">
          <h4>Files ({attachments.length})</h4>
          {loading ? (
            <p>Loading attachments...</p>
          ) : attachments.length === 0 ? (
            <p className="no-attachments">No attachments yet</p>
          ) : (
            <ul>
              {attachments.map((attachment) => (
                <li key={attachment.id} className="attachment-item">
                  <div className="attachment-info">
                    <div className="attachment-name">
                      <span className="file-icon">📄</span>
                      <span className="filename">{attachment.original_filename}</span>
                    </div>
                    <div className="attachment-details">
                      <span className="file-size">
                        {(attachment.size / 1024).toFixed(2)} KB
                      </span>
                      <span className="uploaded-by">
                        by {attachment.first_name} {attachment.last_name}
                      </span>
                      <span className="upload-date">
                        {new Date(attachment.uploaded_at).toLocaleDateString()}
                      </span>
                      {isAdmin && attachment.downloaded_count > 0 && (
                        <span className="download-count">
                          ↓ {attachment.downloaded_count}
                        </span>
                      )}
                    </div>
                  </div>
                  <div className="attachment-actions">
                    {attachment.extension.toLowerCase() === 'pdf' && (
                      <button
                        className="view-button"
                        onClick={() => viewAttachment(attachment)}
                        title="View file in new tab"
                      >
                        View
                      </button>
                    )}
                    <button
                      className="download-button"
                      onClick={() => downloadAttachment(attachment)}
                      title="Download file"
                    >
                      Download
                    </button>
                  </div>
                </li>
              ))}
            </ul>
          )}
        </div>

        {/* Grant Attachments Section - Only for Organizations */}
        {entityType === 'organization' && (
          <div className="grant-attachments-section">
            <h4>Grant Attachments ({grantAttachments.length})</h4>
            {loadingGrants ? (
              <p>Loading grant attachments...</p>
            ) : grantAttachments.length === 0 ? (
              <p className="no-attachments">No grant attachments yet</p>
            ) : (
              <div className="grant-attachments-grid">
                {grantAttachments.map((attachment) => (
                  <div key={attachment.id} className="grant-attachment-card">
                    <div className="card-header">
                      <span className="file-icon">📄</span>
                      <span className="filename">{attachment.original_filename}</span>
                    </div>
                    <div className="card-body">
                      <div className="grant-info">
                        <strong>Grant:</strong> {attachment.grant_name}
                      </div>
                      <div className="upload-info">
                        <span className="upload-date">
                          {new Date(attachment.uploaded_at).toLocaleDateString()}
                        </span>
                        <span className="file-size">
                          {(attachment.size / 1024).toFixed(2)} KB
                        </span>
                      </div>
                    </div>
                    <div className="card-actions">
                      {attachment.extension.toLowerCase() === 'pdf' && (
                        <button
                          className="view-button"
                          onClick={() => viewAttachment(attachment)}
                          title="View file in new tab"
                        >
                          View
                        </button>
                      )}
                      <button
                        className="download-button"
                        onClick={() => downloadAttachment(attachment)}
                        title="Download file"
                      >
                        Download
                      </button>
                    </div>
                  </div>
                ))}
              </div>
            )}
          </div>
        )}
      </div>
    </div>
  );
};

export default AttachmentsTab;
